import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import { Link } from "react-router-dom";
import { SERVICES } from "../lib/services";
import useSeo from "../hooks/useSeo";
import { SITE_URL } from "../lib/site";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ArrowRight, Loader2, CheckCircle2, Landmark, FileText, TrendingUp, Coins, Home, Mountain } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARKED_ASSETS = [
  { Icon: Landmark, label: "Fixed Deposits", sub: "NRE / NRO" },
  { Icon: FileText, label: "Bonds", sub: "Govt & PSU" },
  { Icon: TrendingUp, label: "Listed Cos", sub: "Stocks / PIS" },
  { Icon: Coins, label: "Gold", sub: "The old anchor" },
  { Icon: Home, label: "Property", sub: "Residential" },
  { Icon: Mountain, label: "Land", sub: "Commercial" },
];

const SHIFTS = [
  {
    n: "01",
    title: "The Tech Reset",
    body:
      "AI and automation are quietly redrawing which jobs survive, including well-paid ones long thought safe.",
  },
  {
    n: "02",
    title: "Covid's Lesson",
    body:
      "A life built over twenty years abroad was disrupted in twenty days. Distance stopped feeling like safety.",
  },
  {
    n: "03",
    title: "Tariff & Trade Wars",
    body:
      "Supply chains, industries and whole job markets reshaped by decisions made far above the individual.",
  },
  {
    n: "04",
    title: "Wars & Conflict",
    body:
      "Stability in regions once considered permanent can no longer be assumed by anyone living there.",
  },
  {
    n: "05",
    title: "Geopolitical Churn",
    body:
      "Visas, residency rules and immigration sentiment shift faster than any five-year plan can absorb.",
  },
  {
    n: "06",
    title: "Pressure on Local Jobs",
    body:
      "When local employment tightens, the foreign worker is rarely the last one protected.",
  },
];

const PLAYBOOK = [
  {
    n: "1",
    title: "Associate with an existing venture",
    body:
      "Take equity or a stake in an Indian Startup or company already in motion — a foothold that exists and grows whether you're in Mumbai or Manchester.",
  },
  {
    n: "2",
    title: "Offer advisory from where you are",
    body:
      "Your international experience is an asset India pays for. Advisory and board roles keep you woven in — earning relevance, not just returns.",
  },
  {
    n: "3",
    title: "Leverage your connections abroad",
    body:
      "Markets, capital, clients and networks outside India are exactly what Indian ventures lack. That bridge is your unfair advantage — make it your stake.",
  },
  {
    n: "4",
    title: "Set up your own entity in India",
    body:
      "A registered company, banking and a payment stack — built remotely, kept warm. Not a someday plan. An operation that already exists under your name.",
  },
];

const ZOZO_OFFER = [
  {
    title: "Company or LLP Registration",
    body: " — set up remotely, with PAN, TAN and full compliance.",
  },
  {
    title: "Banking & Payment Stack",
    body: " — current account and gateway, ready to transact.",
  },
  {
    title: "Registered Office Address & Workspace",
    body: " — a real, compliant base when you need one.",
  },
  {
    title: "Funding & Venture Matchmaking",
    body: " — if you'd rather associate than start solo.",
  },
  {
    title: "One Accountable Team",
    body: " — no vendor juggling, no hidden government fees.",
  },
];

const STATS = [
  { big: "1,200+", label: "Founders served" },
  { big: "Remote", label: "Built without a flight" },
  { big: "Pan-India", label: "Expert network" },
];

export default function NriPage() {
  useSeo({
    title:
      "For NRIs — Build Your Foothold in India, From Abroad | ZOZO Venture",
    description:
      "The new generation of NRIs isn't parking money in India — they're keeping a living foothold alive. Company registration, banking and advisory, built remotely by ZOZO Venture.",
    canonical: `${SITE_URL}/nri`,
    image:
      "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1200",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "For NRIs — Build Your Foothold in India, From Abroad",
        url: `${SITE_URL}/nri`,
        description:
          "Company registration, banking, payment gateways and advisory for Non-Resident Indians — built remotely, kept warm by an accountable team in India.",
        inLanguage: "en-IN",
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1200",
        },
        isPartOf: { "@type": "WebSite", name: "ZOZO Venture", url: `${SITE_URL}/` },
        audience: {
          "@type": "Audience",
          audienceType: "Non-Resident Indians (NRI / OCI / PIO)",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "India Foothold For NRIs — Remote Company, Banking & Advisory",
        serviceType: "NRI / OCI India entity setup, banking and advisory",
        provider: {
          "@type": "Organization",
          name: "ZOZO Venture",
          url: `${SITE_URL}/`,
        },
        areaServed: { "@type": "Country", name: "India" },
        audience: {
          "@type": "Audience",
          audienceType: "Non-Resident Indians (NRI / OCI / PIO)",
        },
        description:
          "End-to-end India presence for NRIs — Private Limited Company / LLP registration with apostilled documents and resident director, current account opening, payment gateway onboarding, registered office, FEMA / RBI compliance and ongoing advisory — built without flying down.",
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "NRI Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "NRI Company or LLP Registration", description: "Remote Private Limited Company or LLP registration for NRIs with apostilled documents, resident director and SPICe+ / FiLLiP filing." } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "NRI Banking & Payment Stack", description: "Current account opening with AD-Cat-I banks and payment gateway onboarding for NRI-owned Indian companies." } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Registered Office & Workspace", description: "Compliant registered office address and on-demand workspace for NRI-owned Indian entities." } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Funding & Venture Matchmaking", description: "Investor matchmaking and venture introductions for NRIs choosing to associate with existing Indian startups." } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "FEMA & RBI Compliance", description: "FEMA / RBI compliance, FC-GPR filing, FIRMS portal and Entity Master Form for NRI-owned Indian companies." } },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "For NRIs", item: `${SITE_URL}/nri` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Can an NRI register a Private Limited Company in India without flying down?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. The MCA incorporation process is fully digital. NRIs can register a Private Limited Company in India remotely with apostilled identity / address proofs, a resident director who has stayed in India for at least 182 days in the previous financial year, and a registered office address. ZOZO Venture coordinates apostille, resident director, SPICe+ filing and bank account opening end-to-end.",
            },
          },
          {
            "@type": "Question",
            name: "Can an NRI or OCI be a director or 100% shareholder in an Indian company?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. NRIs and OCIs can be directors and 100% shareholders in an Indian Private Limited Company in most sectors under the FDI automatic route — including IT, B2B e-commerce, manufacturing, consultancy and many services. A minimum of two directors is required, and at least one must be a resident director.",
            },
          },
          {
            "@type": "Question",
            name: "How does an NRI open a current account for an Indian company from abroad?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Choose an AD-Cat-I private bank like HDFC, ICICI, Kotak or Axis. After incorporation, complete remote video KYC, submit apostilled documents and beneficial-owner declarations, and route the first share capital infusion as inward remittance to the company's account. The resident director typically signs as the operating authority while abroad.",
            },
          },
          {
            "@type": "Question",
            name: "What is FEMA compliance and what does FC-GPR mean for NRIs?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "FEMA (Foreign Exchange Management Act) governs foreign investment in Indian companies. When an NRI infuses share capital, the Indian company must file FC-GPR (Foreign Currency-Gross Provisional Return) with the RBI within 30 days of allotment, register on the FIRMS portal and maintain the Entity Master Form. ZOZO Venture drives this entire compliance flow.",
            },
          },
        ],
      },
    ],
  });

  return (
    <div
      className="min-h-screen bg-white text-[#0A0A0A] font-body"
      data-testid="nri-page"
    >
      <Header />
      <main>
        <Hero />
        <Section1Pattern />
        <Section2Shift />
        <Section3Uncertainty />
        <Section4Playbook />
        <Section5Zozo />
        <Section6Contact />
      </main>
      <ClosingBlock />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="bg-white" data-testid="nri-hero">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center">
        <div className="inline-flex items-center border border-[#EAB308]/40 bg-[#FEF9C3] px-3 py-1.5 reveal" data-testid="nri-hero-pill">
          <span className="text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-[#A16207]">
            Exclusively for Non-Resident Indians
          </span>
        </div>

        <p
          className="mt-8 text-[11px] sm:text-xs font-semibold tracking-[0.32em] uppercase text-black/55 reveal"
          style={{ animationDelay: "60ms" }}
        >
          An old Indian saying
        </p>

        <h1
          className="font-heading font-black tracking-tight leading-[1.05] mt-4 reveal"
          style={{
            animationDelay: "140ms",
            fontSize: "clamp(2rem, 5.2vw, 3.5rem)",
          }}
          data-testid="nri-hero-h1"
        >
          The thing you kept aside — and almost let go —{" "}
          <span className="text-[#EAB308]">
            becomes the thing that saves you
          </span>{" "}
          the day you need it.
        </h1>

        <p
          className="mt-7 text-base sm:text-lg leading-relaxed text-black/70 max-w-2xl mx-auto reveal"
          style={{ animationDelay: "220ms" }}
        >
          There is a Gujarati proverb —{" "}
          <span
            className="text-black"
            style={{ fontFamily: '"Noto Serif Gujarati", "Mukta Vaani", serif' }}
          >
            સંઘરેલો સાપ પણ કામ મા આવે
          </span>
          ,{" "}
          <span className="italic text-[#EAB308] font-medium">
            &quot;sangrelo saap pan kaam maa aave.&quot;
          </span>
        </p>

        <div
          className="mx-auto mt-10 h-[3px] w-16 bg-[#EAB308] reveal"
          style={{ animationDelay: "280ms" }}
          aria-hidden
        />

        <p
          className="mt-10 text-lg sm:text-xl leading-relaxed text-black/80 max-w-3xl mx-auto reveal"
          style={{ animationDelay: "340ms" }}
        >
          For decades, your money worked in India while you worked abroad. That
          was enough.{" "}
          <span className="text-[#EAB308] font-semibold">
            It isn&apos;t anymore.
          </span>{" "}
          The smartest NRIs are no longer parking money in India — they&apos;re
          keeping something alive there.
        </p>
      </div>
    </section>
  );
}

/* ---------- SECTION 1 ---------- */
function Section1Pattern() {
  return (
    <section
      className="border-t border-black/10 bg-white"
      data-testid="nri-section-1"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionTag tag="[ 01 ]" label="The pattern that worked" />
        <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-3xl leading-[1.05]">
          Earn outside.{" "}
          <span className="text-[#EAB308]">Park it back home.</span>
        </h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70">
          For a generation of NRIs, the formula was simple and it never failed:
          earn in a stronger currency, send it to India, and let it sit
          somewhere safe.
        </p>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4 auto-rows-fr">
          {PARKED_ASSETS.map((a, i) => {
            const Icon = a.Icon;
            return (
              <div
                key={a.label}
                className="border border-black/15 bg-white p-5 sm:p-6 flex flex-col h-full reveal"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`asset-card-${i}`}
              >
                <div className="h-10 w-10 flex items-center justify-center bg-[#FEF9C3] text-[#A16207] shrink-0">
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <p className="mt-5 font-heading font-bold text-base sm:text-lg leading-tight">
                  {a.label}
                </p>
                <p className="mt-1 text-xs sm:text-sm text-black/55">{a.sub}</p>
              </div>
            );
          })}
        </div>

        <p className="mt-12 text-base sm:text-lg leading-relaxed text-black/75">
          It worked because the world was{" "}
          <b className="text-black">predictable</b> — and notice that every one
          of these, even shares in listed companies, is the{" "}
          <b className="text-black">same move</b>: money sent to sit and grow.
          None of them gives you an operation, a footing, or a life to step
          back into. India was a vault, not a plan.{" "}
          <b className="text-black">That predictability is gone.</b>
        </p>
      </div>
    </section>
  );
}

/* ---------- SECTION 2 ---------- */
function Section2Shift() {
  return (
    <section
      className="border-t border-black/10 bg-white"
      data-testid="nri-section-2"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionTag tag="[ 02 ]" label="Then the ground shifted" />
        <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-3xl leading-[1.05]">
          One Decade.{" "}
          <span className="text-[#EAB308]">Every assumption rewritten.</span>
        </h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70">
          The pattern didn&apos;t break slowly. It broke all at once, from
          directions no one was watching.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 auto-rows-fr border border-black/15">
          {SHIFTS.map((s, i) => (
            <div
              key={s.n}
              className={`p-6 sm:p-7 flex flex-col h-full reveal ${
                i < SHIFTS.length - 1 ? "border-b md:border-b-0" : ""
              } ${i % 3 !== 2 ? "md:border-r" : ""} ${
                i < 3 ? "md:border-b" : ""
              } border-black/15`}
              style={{ animationDelay: `${i * 70}ms` }}
              data-testid={`shift-${s.n}`}
            >
              <p className="font-heading font-black text-2xl text-[#EAB308] leading-none">
                {s.n}
              </p>
              <h3 className="mt-4 font-heading font-bold text-lg sm:text-xl leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-sm sm:text-[15px] leading-relaxed text-black/70">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 3 ---------- */
function Section3Uncertainty() {
  return (
    <section
      className="border-t border-black/10 bg-[#F4F4F5]"
      data-testid="nri-section-3"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
        <SectionTag
          tag="[ 03 ]"
          label="Where that leaves you"
          centered
        />
        <h2
          className="font-heading font-black tracking-tight leading-[1.02] mt-5"
          style={{ fontSize: "clamp(2.25rem, 6vw, 4.25rem)" }}
          data-testid="nri-section-3-h2"
        >
          The only thing now{" "}
          <span className="line-through decoration-black/40 decoration-4">
            certain
          </span>{" "}
          is <span className="text-[#EAB308]">uncertainty.</span>
        </h2>
        <div className="mt-9 text-base sm:text-lg leading-relaxed text-black/75 max-w-2xl mx-auto space-y-3">
          <p>
            <b className="text-black">A Fixed Deposit</b> cannot employ you.
          </p>
          <p>
            <b className="text-black">A Stock Portfolio</b> cannot give you a
            desk to walk into.
          </p>
          <p>
            <b className="text-black">A Land</b> cannot introduce you to a
            market.
          </p>
          <p>
            <b className="text-black">A Parked Asset</b> is not a fallback —
            it&apos;s just money waiting.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 4 ---------- */
function Section4Playbook() {
  return (
    <section
      className="border-t border-black/10 bg-white"
      data-testid="nri-section-4"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionTag tag="[ 04 ]" label="What smart NRIs do now" />
        <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-3xl leading-[1.05]">
          Don&apos;t park money.{" "}
          <span className="text-[#EAB308]">Keep something alive.</span>
        </h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70">
          The new generation of NRIs isn&apos;t waiting passively. They are
          building a living foothold in India — small, real, and running —
          long before they ever need it.
        </p>

        {/* Brand band */}
        <div
          className="mt-12 border border-black/15 bg-white px-6 sm:px-10 py-10 sm:py-12"
          data-testid="nri-brand-band"
        >
          <p className="text-center text-[10px] sm:text-[11px] font-semibold tracking-[0.24em] uppercase text-black/55">
            In the last decade, well-known Startups built in India like
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6 items-center justify-items-center">
            <BrandText name="zerodha" color="#387ED1" />
            <BrandText name="zomato" color="#E23744" />
            <ZohoWordmark />
            <BrandText name="zepto" color="#6A2DE0" />
          </div>
          <div className="mt-10 pt-6 border-t border-black/10 text-center">
            <p className="text-sm sm:text-[15px] text-black/70 leading-relaxed max-w-xl mx-auto">
              Built in India, by Indians — proof of what becomes possible when
              you stay connected to the ecosystem.
            </p>
            <p className="mt-4 text-[11px] text-black/40 leading-relaxed max-w-xl mx-auto">
              Brand names shown for illustration only. All trademarks belong to
              their respective owners; no affiliation implied.
            </p>
          </div>
        </div>

        {/* Numbered playbook */}
        <div className="mt-14 border-t border-black/15">
          {PLAYBOOK.map((p, i) => (
            <div
              key={p.n}
              className="grid grid-cols-[auto_1fr] gap-x-6 sm:gap-x-10 items-start py-8 sm:py-10 border-b border-black/15 reveal"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`playbook-${p.n}`}
            >
              <div className="w-12 sm:w-16 shrink-0">
                <p
                  className="font-heading font-black text-[#EAB308] leading-none"
                  style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}
                >
                  {p.n}
                </p>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl sm:text-2xl leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-black/70 max-w-3xl">
                  {p.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Real payoff callout */}
        <div
          className="mt-14 border-l-[6px] border-[#EAB308] bg-[#FEF9C3] px-6 py-7 sm:px-8 sm:py-8"
          data-testid="nri-real-payoff"
        >
          <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.24em] uppercase text-[#A16207]">
            The Real Payoff
          </p>
          <p className="mt-3 text-lg sm:text-xl leading-relaxed text-black/85">
            So that the day you return to India —{" "}
            <span className="italic text-[#EAB308] font-semibold">
              by choice, or because you had no choice
            </span>{" "}
            — you don&apos;t start from zero. You step into something already
            running.
          </p>
        </div>
      </div>
    </section>
  );
}

function BrandText({ name, color }) {
  return (
    <span
      className="font-heading font-black tracking-tight lowercase leading-none"
      style={{ color, fontSize: "clamp(1.85rem, 3.2vw, 2.5rem)" }}
    >
      {name}
    </span>
  );
}

function ZohoWordmark() {
  const letters = [
    { c: "z", color: "#E42527" },
    { c: "o", color: "#F9B21D" },
    { c: "h", color: "#089949" },
    { c: "o", color: "#226DB4" },
  ];
  return (
    <span
      className="font-heading font-black tracking-tight lowercase inline-flex leading-none"
      style={{ fontSize: "clamp(1.85rem, 3.2vw, 2.5rem)" }}
      aria-label="zoho"
    >
      {letters.map((l, i) => (
        <span key={`${l.color}-${i}`} style={{ color: l.color }}>
          {l.c}
        </span>
      ))}
    </span>
  );
}

/* ---------- SECTION 5 ---------- */
function Section5Zozo() {
  return (
    <section
      className="border-t border-black/10 bg-white"
      data-testid="nri-section-5"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionTag tag="[ 05 ]" label="Where ZOZO comes in" />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Left */}
          <div className="lg:col-span-7">
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight max-w-2xl leading-[1.05]">
              Your foothold in India,{" "}
              <span className="text-[#EAB308]">built from abroad.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70 max-w-2xl">
              You don&apos;t need to fly down, chase paperwork, or trust a
              stranger on the ground. ZOZO is the accountable team that builds
              and keeps your India presence alive — while you stay where you
              are.
            </p>

            <ul className="mt-8 border-t border-black/15">
              {ZOZO_OFFER.map((item, i) => (
                <li
                  key={item.title}
                  className="grid grid-cols-[auto_1fr] items-start gap-3 py-5 border-b border-black/15 reveal"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`zozo-offer-${i}`}
                >
                  <ArrowRight
                    size={18}
                    className="text-[#EAB308] mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-base leading-relaxed text-black/85">
                    <b className="text-black font-bold">{item.title}</b>
                    {item.body}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — stat panel */}
          <div className="lg:col-span-5">
            <div className="border border-black/15">
              {STATS.map((s, i) => (
                <div
                  key={s.big}
                  className={`px-6 sm:px-7 py-7 sm:py-8 grid grid-cols-[auto_1fr] items-center gap-x-6 ${
                    i > 0 ? "border-t border-black/15" : ""
                  }`}
                  data-testid={`stat-${i}`}
                >
                  <p
                    className="font-heading font-black tracking-tight leading-none text-black whitespace-nowrap"
                    style={{ fontSize: "clamp(1.5rem, 2.4vw, 1.85rem)" }}
                  >
                    {s.big}
                  </p>
                  <p className="text-xs sm:text-sm uppercase tracking-[0.14em] text-black/55 leading-snug text-right">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Reassurance box */}
        <div
          className="mt-12 border border-black/15 bg-[#F4F4F5] px-6 py-7 sm:px-8 sm:py-8"
          data-testid="nri-reassurance"
        >
          <p className="text-base sm:text-lg leading-relaxed text-black/85">
            <b className="text-black">But can an NRI even do this?</b> Yes. NRIs
            can fully own and operate an Indian private limited company while
            living abroad. We handle FEMA / RBI compliance, remote KYC and
            document attestation end-to-end — you don&apos;t need to be in
            India to begin.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- SECTION 6 — Contact form ---------- */
function Section6Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || "Not provided",
        city: form.city.trim(),
        service_interest: "NRI Foothold Inquiry",
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Couldn't submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="nri-contact"
      className="border-t border-black/10 bg-white"
      data-testid="nri-section-6"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <SectionTag tag="[ 06 ]" label="Start the conversation" centered />
        <h2
          className="font-heading font-black tracking-tight leading-[1.05] mt-5 text-center"
          style={{ fontSize: "clamp(2rem, 4.8vw, 3.25rem)" }}
        >
          Build it now.{" "}
          <span className="text-[#EAB308]">Before you need it.</span>
        </h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70 max-w-2xl mx-auto text-center">
          Tell us where you are and what you have in mind — a ZOZO Advisor
          will reach out within 24 hours with a clear, tailored plan.
        </p>

        <div className="mt-10 max-w-[560px] mx-auto">
          {submitted ? (
            <SuccessState />
          ) : (
            <form
              onSubmit={onSubmit}
              className="space-y-5"
              data-testid="nri-contact-form"
              noValidate
            >
              <FormField id="nri-name" label="Name" required>
                <Input
                  id="nri-name"
                  data-testid="nri-input-name"
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="Your full name"
                  className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                  required
                />
              </FormField>

              <FormField id="nri-email" label="Email Id" required>
                <Input
                  id="nri-email"
                  data-testid="nri-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="you@example.com"
                  className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                  required
                />
              </FormField>

              <FormField id="nri-phone" label="Contact No.">
                <Input
                  id="nri-phone"
                  data-testid="nri-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder="+44 20 1234 5678"
                  className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                />
              </FormField>

              <FormField id="nri-city" label="City (where you live now)">
                <Input
                  id="nri-city"
                  data-testid="nri-input-city"
                  value={form.city}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, city: e.target.value }))
                  }
                  placeholder="Dubai, London, Singapore, San Francisco…"
                  className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-12"
                />
              </FormField>

              <FormField id="nri-message" label="Message">
                <Textarea
                  id="nri-message"
                  data-testid="nri-input-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, message: e.target.value }))
                  }
                  placeholder="Tell us where you live, what you're thinking about, and any timeline you have in mind."
                  className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black"
                />
              </FormField>

              <button
                type="submit"
                data-testid="nri-submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#EAB308] text-white px-6 py-3.5 font-heading font-bold tracking-wide uppercase hover:bg-black transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={16} /> Sending…
                  </>
                ) : (
                  <>Request a callback →</>
                )}
              </button>

              <p className="text-center text-sm text-black/55">
                Or write directly to{" "}
                <a
                  href="mailto:info@zozoventure.com"
                  className="text-[#EAB308] hover:underline"
                >
                  info@zozoventure.com
                </a>
              </p>
            </form>
          )}
        </div>

        <p className="mt-10 text-center text-sm text-black/55 max-w-xl mx-auto leading-relaxed">
          If you&apos;re serious about a foothold in India, one conversation is
          enough to know if we&apos;re the right team. That&apos;s all we ask
          for.
        </p>
      </div>
    </section>
  );
}

function FormField({ id, label, required, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold tracking-[0.15em] uppercase text-black/70 mb-2"
      >
        {label} {required && <span className="text-[#EAB308]">*</span>}
      </label>
      {children}
    </div>
  );
}

function SuccessState() {
  return (
    <div
      className="border border-[#EAB308] bg-[#FEF9C3] p-8 text-center"
      data-testid="nri-contact-success"
    >
      <CheckCircle2
        size={36}
        className="text-[#EAB308] mx-auto"
        strokeWidth={2}
      />
      <h3 className="mt-4 font-heading font-black text-2xl tracking-tight">
        Thank you — we&apos;ll be in touch.
      </h3>
      <p className="mt-3 text-sm sm:text-base leading-relaxed text-black/75">
        A ZOZO Advisor will reach out within 24 hours. Meanwhile, you can also
        write directly to{" "}
        <a
          href="mailto:info@zozoventure.com"
          className="font-semibold text-[#EAB308] hover:underline"
        >
          info@zozoventure.com
        </a>
        .
      </p>
    </div>
  );
}

/* ---------- Section Tag helper ---------- */
function SectionTag({ tag, label, centered }) {
  return (
    <div
      className={`flex items-center gap-3 ${centered ? "justify-center" : ""}`}
    >
      <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#EAB308]">
        {tag}
      </span>
      <span className="text-xs font-semibold tracking-[0.22em] uppercase text-black/55">
        {label}
      </span>
    </div>
  );
}

/* ---------- Closing block ---------- */
function ClosingBlock() {
  const year = new Date().getFullYear();
  const pills = SERVICES.slice(0, 5).map((s) => s.title);
  return (
    <footer
      className="bg-white text-black border-t border-black/15"
      data-testid="nri-closing-block"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 text-center">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 bg-[#EAB308]" />
          <span className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#EAB308]">
            ZOZO VENTURE
          </span>
        </Link>

        <h2 className="mt-8 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
          Everything a new founder needs.{" "}
          <span className="text-[#EAB308]">In one place.</span>
        </h2>

        <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/70 max-w-3xl mx-auto">
          From Startup Funding, Company Registration, Banking, Payment
          Gateways and Office Workspace — delivered by an experienced team of
          industry experts.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {pills.map((p) => (
            <span
              key={p}
              className="inline-flex items-center border border-black/20 px-3 py-1.5 text-xs sm:text-sm text-black/75"
            >
              {p}
            </span>
          ))}
        </div>

        <p className="mt-14 text-xs text-black/45">
          © {year} ZOZO Venture. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
